import PropTypes from 'prop-types'
import React, { useEffect, useMemo } from 'react'
// Component
import { EmptyText } from '../Common/CellText'
import NormalTag from '../NormalTag/NormalTag'
// Styled component
import {
  PopoverItem,
  PopoverList,
  PopoverTitle,
  TagWrapper,
  ViewLogModal,
} from './PopoverWatchTimeStyled'
// Ant design
import {
  BankOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  HistoryOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Popover } from 'antd'
// Other
import { inject, observer } from 'mobx-react'
import { useState } from 'react'
import { v4 } from 'uuid'
import { blue, green, yellowPrimary } from '../../color'
import { ASSIGNEE_TYPE } from '../../constants'
import date_format from '../../date_format'
import useOpenItem from '../../hooks/useOpenItem'
import utils from '../../utils'
import SpinnerInlineComponent from '../SpinnerInlineComponent'

const findAssigneeType = assigneeType => {
  switch (assigneeType) {
    case ASSIGNEE_TYPE.USER:
      return <UserOutlined style={{ color: blue, marginRight: 6 }} />
    case ASSIGNEE_TYPE.DEPARTMENT:
      return <BankOutlined style={{ color: green, marginRight: 6 }} />
    case ASSIGNEE_TYPE.GROUP:
      return <TeamOutlined style={{ color: yellowPrimary, marginRight: 6 }} />
    default:
      return <UserOutlined style={{ color: blue, marginRight: 6 }} />
  }
}
const findTextColor = assignee_type => {
  return assignee_type === ASSIGNEE_TYPE.USER
    ? blue
    : assignee_type === ASSIGNEE_TYPE.DEPARTMENT
      ? green
      : yellowPrimary
}

const PopoverWatchTime = props => {
  const {
    userList = [],
    title,
    showFullTag,
    internalDocumentStore,
    selectPeopleStore,
    userGroupStore,
    isFullViewer,
    showViewLogLabel,
    typeId,
    compared_date,
    created_at = '',
    departWithUsers,
  } = props

  const { viewLogList, setViewLogList, getViewLog } = internalDocumentStore
  const {
    isOpen: isVisibleViewLog,
    handleOpen: handelVisibleLogView,
    handleClose: handleCancelLogView,
  } = useOpenItem()
  const [isOpenPopoverList, setIsOpenPopoverList] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const handlePopoverList = value => {
    setIsOpenPopoverList(value)
  }

  useEffect(() => {
    if (selectPeopleStore.originDepartmentWithUserList.length === 0) {
      selectPeopleStore.getDepartmentWithUsers()
    }
  }, [])

  const createdAt = new Date(created_at)
  const comparedDate = new Date(compared_date)

  const enrichedUserList = useMemo(() => {
    const list = Array.isArray(userList) ? [...userList] : []

    const comments = internalDocumentStore?.internalDocumentCommentList || []
    let commentNames = []
    for (const c of comments) {
      const content = c?.content || ''
      const match = content.match(/Đã thêm người theo dõi:\s*(.+)/i)
      if (match && match[1]) {
        commentNames = match[1]
          .split(',')
          .map(s => s.trim())
          .filter(Boolean)
        break
      }
    }

    return list.map((user, idx) => {
      let resolvedName = user.full_name || user.name || user.name_uppercase || ''

      if (!resolvedName && (user.id || user.username)) {
        const uId = user.id || user.username
        const userInfo = utils.getInfoUser(
          uId,
          departWithUsers || selectPeopleStore.originDepartmentWithUserList
        )
        if (userInfo?.full_name) {
          resolvedName = userInfo.full_name
        }
      }

      if (!resolvedName && user.assignee_type !== ASSIGNEE_TYPE.USER) {
        const gId = String(user.id || user.assignee_code || '')
        const gMatch =
          selectPeopleStore.originGroupList?.find(
            g => String(g.id) === gId
          ) ||
          userGroupStore?.dataGroupStoreList?.find(
            g => String(g.id_group) === gId
          )
        if (gMatch?.name || gMatch?.name_group) {
          resolvedName = gMatch.name || gMatch.name_group
        }
      }

      if (!resolvedName && commentNames.length > 0) {
        if (user.assignee_type !== ASSIGNEE_TYPE.USER) {
          const gName = commentNames.find(
            n =>
              /nhóm|chi bộ|đảng|đoàn|phòng|ban/i.test(n) ||
              n.toLowerCase().includes('chi bộ')
          )
          if (gName) {
            resolvedName = gName
          } else if (commentNames[idx]) {
            resolvedName = commentNames[idx]
          }
        } else {
          const userCommentNames = commentNames.filter(
            n => !/nhóm|chi bộ|đảng|đoàn|phòng|ban/i.test(n)
          )
          const userIdx = list
            .filter(u => u.assignee_type === ASSIGNEE_TYPE.USER)
            .indexOf(user)
          if (userIdx >= 0 && userCommentNames[userIdx]) {
            resolvedName = userCommentNames[userIdx]
          } else if (commentNames[idx]) {
            resolvedName = commentNames[idx]
          }
        }
      }

      return {
        ...user,
        full_name: resolvedName || user.id || user.username || '',
        name: resolvedName || user.id || user.username || '',
      }
    })
  }, [
    userList,
    departWithUsers,
    selectPeopleStore.originDepartmentWithUserList,
    selectPeopleStore.originGroupList,
    userGroupStore?.dataGroupStoreList,
    internalDocumentStore?.internalDocumentCommentList,
  ])

  const groups = useMemo(
    () =>
      enrichedUserList.filter(
        item => item.assignee_type !== ASSIGNEE_TYPE.USER
      ),
    [enrichedUserList]
  )

  const usersPre = useMemo(
    () =>
      enrichedUserList.filter(
        item => item.assignee_type === ASSIGNEE_TYPE.USER
      ),
    [enrichedUserList]
  )

  const handleGetViewLog = async groupsToFetch => {
    setIsLoading(true)
    setViewLogList([])
    try {
      const promises = []
      const targetGroups = groupsToFetch || groups
      if (targetGroups && targetGroups.length > 0) {
        targetGroups.forEach(group => {
          if (group.id) {
            promises.push(
              getViewLog({
                assignee_code: group.id,
                assignee_type: group.assignee_type || 'GRP',
                document_code: typeId,
              })
            )
          }
        })
      }

      if (usersPre && usersPre.length > 0) {
        const userCodes = usersPre
          .map(user => user.id || user.username)
          .filter(Boolean)
          .join(',')
        if (userCodes) {
          promises.push(
            getViewLog({
              assignee_code: userCodes,
              assignee_type: ASSIGNEE_TYPE.USER,
              document_code: typeId,
            })
          )
        }
      }

      await Promise.all(promises)
    } catch (error) {
      console.error('Error fetching view log:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const getGroupLogList = group => {
    const groupIdStr = String(group.id || '')
    const memberLogs = (viewLogList || []).filter(
      item => String(item.groupId) === groupIdStr
    )

    if (memberLogs.length > 0) {
      return memberLogs.map(member => {
        let memberName =
          member.full_name ||
          member.name ||
          utils.getInfoUser(
            member.username,
            departWithUsers || selectPeopleStore.originDepartmentWithUserList
          )?.full_name ||
          member.username

        return {
          ...member,
          full_name: memberName,
          assignee_type: member.assignee_type || ASSIGNEE_TYPE.USER,
        }
      })
    }

    return [
      {
        ...group,
        username: group.id,
        full_name: group.full_name || group.name,
        assignee_type: group.assignee_type || 'GRP',
        first_date_read: group.last_date_read || null,
        created_at: group.created_at || created_at,
        isGroupSummary: true,
      },
    ]
  }

  const getUserLogList = () => {
    const logs = (viewLogList || []).filter(
      item => item.groupType === ASSIGNEE_TYPE.USER
    )

    if (!usersPre || usersPre.length === 0) {
      return logs
    }

    return usersPre.map(assignedUser => {
      const uCode = String(
        assignedUser.id || assignedUser.username || ''
      ).toLowerCase()
      const matchedLog = logs.find(
        log => String(log.username || '').toLowerCase() === uCode
      )

      if (matchedLog) {
        return {
          ...assignedUser,
          ...matchedLog,
          full_name:
            assignedUser.full_name ||
            matchedLog.full_name ||
            matchedLog.name ||
            assignedUser.name ||
            assignedUser.id,
          created_at:
            matchedLog.created_at || assignedUser.created_at || created_at,
          first_date_read:
            matchedLog.first_date_read ||
            matchedLog.last_date_read ||
            assignedUser.last_date_read ||
            null,
        }
      }

      return {
        ...assignedUser,
        username: assignedUser.id || assignedUser.username,
        assignee_type: ASSIGNEE_TYPE.USER,
        first_date_read: assignedUser.last_date_read || null,
        created_at: assignedUser.created_at || created_at,
      }
    })
  }

  const popoverList = (listSelected, selectGroup) => {
    let key = v4()
    return (
      <PopoverList key={key}>
        <div className={'popover-head'}>
          {selectGroup ? (
            <span
              style={{
                color: findTextColor(selectGroup.assignee_type),
                textTransform: 'capitalize',
                marginLeft: selectGroup ? '8px' : 0,
                maxWidth: 160,
                minWidth: 160,
              }}>
              {findAssigneeType(selectGroup.assignee_type)}
              {utils.getNameInCapitalize(
                selectGroup?.full_name || selectGroup?.name || 'Nhóm'
              )}
            </span>
          ) : (
            <b
              style={{
                display: 'inline-block',
                marginLeft: '8px',
                maxWidth: 160,
                minWidth: 160,
              }}>
              <UserOutlined style={{ color: blue, marginRight: 6 }} />
              Người dùng
            </b>
          )}

          <b style={{ display: 'inline-block', width: 155 }}>
            <HistoryOutlined style={{ color: blue, marginRight: 6 }} />
            Thời gian nhận
          </b>

          <b style={{ display: 'inline-block', width: 132 }}>
            <HistoryOutlined style={{ color: blue, marginRight: 6 }} />
            Thời gian xem
          </b>
        </div>

        {listSelected?.length > 0 ? (
          listSelected.map((el, index) => {
            let type = findAssigneeType(el?.assignee_type)
            let rawName =
              el?.full_name ||
              el?.name ||
              utils.getInfoUser(
                el?.username,
                departWithUsers || selectPeopleStore.originDepartmentWithUserList
              )?.full_name ||
              enrichedUserList?.find(
                u =>
                  (u.id &&
                    String(u.id).toLowerCase() ===
                      String(el?.username).toLowerCase()) ||
                  (u.username &&
                    String(u.username).toLowerCase() ===
                      String(el?.username).toLowerCase())
              )?.full_name ||
              el?.username ||
              ''
            let name = utils.getNameInCapitalize(rawName) || rawName

            return (
              <PopoverItem key={index}>
                <span
                  style={{
                    color: blue,
                    marginLeft: '8px',
                    minWidth: 180,
                    maxWidth: 180,
                  }}>
                  {type}
                  {el?.position_name === 'Văn thư' ? (
                    <span style={{ color: 'black' }}>{name}</span>
                  ) : (
                    <span>{name}</span>
                  )}
                </span>
                <span
                  style={{
                    minWidth: 132,
                  }}>
                  <span>
                    {el.created_at
                      ? date_format.HH_mm_DD_MM_YYYY(el.created_at)
                      : '-'}
                  </span>
                </span>
                <time>
                  {isFullViewer && createdAt < comparedDate ? (
                    <span>
                      <EyeOutlined style={{ color: green, marginRight: 6 }} />
                      Đã xem
                    </span>
                  ) : el.last_date_read || el.first_date_read ? (
                    <span>
                      <EyeOutlined style={{ color: green, marginRight: 6 }} />
                      {date_format.HH_mm_DD_MM_YYYY(
                        el.last_date_read || el.first_date_read
                      )}
                    </span>
                  ) : (
                    <EmptyText>
                      <EyeInvisibleOutlined
                        style={{ color: '#ccc', marginRight: 6 }}
                      />
                      Chưa xem.
                    </EmptyText>
                  )}
                </time>
              </PopoverItem>
            )
          })
        ) : (
          <EmptyText>
            <span style={{ marginLeft: 12 }}>Không có.</span>
          </EmptyText>
        )}
        {isLoading && (
          <div style={{ height: 150, position: 'relative' }}>
            <SpinnerInlineComponent
              sizeSpin={'small'}
              tip="Đang tải dữ liệu..."
              isLoading={isLoading}
              backGround={'#ffffffc7'}
            />
          </div>
        )}
      </PopoverList>
    )
  }

  return (
    <>
      {isFullViewer ? (
        <TagWrapper
          onClick={() => {
            handleGetViewLog(groups)
            handelVisibleLogView()
          }}>
          <NormalTag
            noHover={true}
            showFullTag={showFullTag}
            tags={
              showViewLogLabel
                ? ['Xem nhật ký']
                : enrichedUserList.map(user =>
                    utils.getNameInCapitalize(
                      user?.full_name || user?.name || user?.id
                    )
                  )
            }
          />
        </TagWrapper>
      ) : (
        <Popover
          placement="top"
          content={() => popoverList(enrichedUserList)}
          title={<PopoverTitle>{title}</PopoverTitle>}
          visible={isOpenPopoverList}
          onVisibleChange={handlePopoverList}
          trigger="click"
          overlayClassName={'wrapper-list'}
          overlayStyle={{ maxWidth: '570px' }}>
          <TagWrapper>
            <NormalTag
              noHover={true}
              showFullTag={showFullTag}
              tags={enrichedUserList.map(user =>
                utils.getNameInCapitalize(user?.full_name || user?.name || user?.id)
              )}
            />
          </TagWrapper>
        </Popover>
      )}

      <ViewLogModal
        title={'Danh sách người xem'}
        width={570}
        style={{ top: 40 }}
        visible={isVisibleViewLog}
        closable={true}
        maskClosable={true}
        onCancel={() => {
          handleCancelLogView()
          setViewLogList([])
        }}
        footer={null}>
        <div style={{ background: '#fff', borderRadius: 12 }}>
          {groups.map(group =>
            popoverList(
              getGroupLogList(group),
              group
            )
          )}
          {usersPre.length > 0 &&
            popoverList(
              getUserLogList()
            )
          }
        </div>
      </ViewLogModal>
    </>
  )
}

PopoverWatchTime.propTypes = {
  userList: PropTypes.array,
  title: PropTypes.string,
  showFullTag: PropTypes.bool,
  showViewLogLabel: PropTypes.bool,
}

export default inject(
  'internalDocumentStore',
  'selectPeopleStore',
  'userGroupStore'
)(observer(PopoverWatchTime))
